import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
import { format as formatLocal } from 'date-fns';

// CipherOne's default timezone
const TIMEZONE = 'America/Chicago'; // CST/CDT (UTC-6/UTC-5)

/**
 * Convert UTC date to Central Time and format for display
 * @param {string|Date} utcDate - UTC date string or Date object
 * @param {string} formatStr - date-fns format string
 * @returns {string} Formatted date in Central Time
 */
export const formatInCST = (utcDate, formatStr = 'MMM d, yyyy h:mm a') => {
  if (!utcDate) return 'N/A';
  
  try {
    // Ensure ISO strings without 'Z' are parsed as UTC, not local time
    let date;
    if (typeof utcDate === 'string') {
      const raw = utcDate.trim();
      date = new Date(/[zZ]$|[+-]\d{2}:?\d{2}$/.test(raw) ? raw : raw + 'Z');
    } else {
      date = utcDate;
    }
    return formatInTimeZone(date, TIMEZONE, formatStr);
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
};

/**
 * Format a DATE-ONLY / calendar date (issue_date, due_date, agreement dates).
 * These are calendar dates, NOT instants: a value of YYYY-MM-01 must always
 * render as the 1st regardless of the viewer's timezone. We therefore read the
 * calendar Y-M-D and format it locally, never converting through UTC (which was
 * shifting first-of-month dates back to the previous day in Central).
 * @param {string|Date} value - "YYYY-MM-DD", "YYYY-MM-DDTHH:mm:ss[Z]" or Date
 * @param {string} formatStr - date-fns format string
 * @returns {string} Formatted calendar date
 */
export const formatDateOnly = (value, formatStr = 'MMM d, yyyy') => {
  if (!value) return 'N/A';
  try {
    let y, m, d;
    if (typeof value === 'string') {
      const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (match) {
        y = Number(match[1]); m = Number(match[2]); d = Number(match[3]);
      }
    }
    if (y === undefined) {
      const dt = value instanceof Date ? value : new Date(value);
      if (isNaN(dt.getTime())) return 'Invalid date';
      y = dt.getFullYear(); m = dt.getMonth() + 1; d = dt.getDate();
    }
    // Reject impossible calendar dates (e.g. Feb 30) instead of silently rolling
    // them forward into the next month.
    if (m < 1 || m > 12 || d < 1 || d > 31) return 'Invalid date';
    const local = new Date(y, m - 1, d, 12, 0, 0);
    if (local.getFullYear() !== y || local.getMonth() !== m - 1 || local.getDate() !== d) {
      return 'Invalid date';
    }
    // Local date at noon: no instant conversion, safe across DST and negative offsets.
    return formatLocal(local, formatStr);
  } catch (error) {
    return 'Invalid date';
  }
};

/**
 * Calendar-day overdue count for a DATE-ONLY due date, evaluated against the
 * current Central (America/Chicago) calendar day. An invoice due today is NOT
 * overdue (returns 0). Dates are compared as calendar days, never as instants,
 * so a due date stored as YYYY-MM-01T00:00:00Z is not pulled back to the prior
 * day in Central. Returns 0 for missing/invalid dates and for future due dates.
 * @param {string|Date} dueValue - due date (date-only or ISO)
 * @param {Date} now - instant to evaluate "today" from (defaults to now)
 * @returns {number} whole calendar days overdue (>= 0)
 */
export const daysOverdue = (dueValue, now = new Date()) => {
  if (!dueValue) return 0;
  // Parse the due value as a calendar date (Y-M-D), same rules as formatDateOnly.
  let y, m, d;
  if (typeof dueValue === 'string') {
    const match = dueValue.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) { y = Number(match[1]); m = Number(match[2]); d = Number(match[3]); }
  }
  if (y === undefined) {
    const dt = dueValue instanceof Date ? dueValue : new Date(dueValue);
    if (isNaN(dt.getTime())) return 0;
    y = dt.getFullYear(); m = dt.getMonth() + 1; d = dt.getDate();
  }
  if (m < 1 || m > 12 || d < 1 || d > 31) return 0;
  const due = new Date(y, m - 1, d, 12, 0, 0);
  // Today's Central calendar date, read as Y-M-D then rebuilt as a local noon date.
  const centralToday = formatInTimeZone(now, TIMEZONE, 'yyyy-MM-dd');
  const [ty, tm, td] = centralToday.split('-').map(Number);
  const today = new Date(ty, tm - 1, td, 12, 0, 0);
  const diff = Math.round((today - due) / 86400000);
  return diff > 0 ? diff : 0;
};

/**
 * Get current time in CST
 * @returns {Date} Current time in CST
 */
export const nowInCST = () => {
  return toZonedTime(new Date(), TIMEZONE);
};

/**
 * Format date with timezone indicator
 * @param {string|Date} utcDate - UTC date string or Date object
 * @param {string} formatStr - date-fns format string
 * @returns {string} Formatted date with the CST/CDT indicator for that instant
 */
export const formatWithTimezone = (utcDate, formatStr = 'MMM d, yyyy h:mm a') => {
  return formatInCST(utcDate, `${formatStr} zzz`);
};
